// Shared audit/inspection status workflow (guided + reopen) used by the detail page and the list.
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, RotateCcw, Loader2, ChevronDown, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export const STATUS_TONE = { Draft: "amber", Submitted: "blue", Approved: "green", Rejected: "red" };
export const APPROVER_ROLES = new Set(["director", "administrator"]);

// from -> [{to, label, kind}]
const TRANSITIONS = {
    Draft: [{ to: "Submitted", label: "Submit", kind: "submit" }],
    Submitted: [{ to: "Approved", label: "Approve", kind: "approve" }, { to: "Rejected", label: "Reject", kind: "reject" }],
    Approved: [{ to: "Draft", label: "Reopen", kind: "reopen" }],
    Rejected: [{ to: "Draft", label: "Reopen", kind: "reopen" }],
};
// Client-owned report, no PR signatory on the template: Submitted is the completion
// state — no Approve/Reject offered, only a straight reopen back to Draft.
const NO_GATE_TRANSITIONS = {
    Draft: [{ to: "Submitted", label: "Submit", kind: "submit" }],
    Submitted: [{ to: "Draft", label: "Reopen", kind: "reopen" }],
};
const APPROVER_ONLY = new Set(["Submitted>Approved", "Submitted>Rejected", "Approved>Draft"]);

// `instance.approval_gate` is computed server-side (see backend `_approval_gate`) — True
// (or absent, for safety) means PR approval still applies; False means the report is
// client-owned with no PR signatory and Submitted is already the completed state.
// `instance.can_approve` is computed server-side per requesting user (see
// `services/review_routing.compute_can_approve`) — blocks approving your own
// submission and, when a report type has configured routing (Settings → Report
// Review), restricts Approve/Reject/Reopen to the named reviewer(s) instead of any
// director/administrator.
export function transitionsFor(status, role, instance) {
    const gate = !instance || instance.approval_gate !== false;
    const table = gate ? TRANSITIONS : NO_GATE_TRANSITIONS;
    return (table[status] || []).filter((t) => {
        if (APPROVER_ONLY.has(`${status}>${t.to}`)) return instance?.can_approve !== false;
        return true;   // edit-level transitions; backend still enforces edit access
    });
}

const KIND_BTN = {
    submit: { bg: "#3F7D3A", color: "#fff", Icon: Send },
    approve: { bg: "#2E8B3D", color: "#fff", Icon: CheckCircle },
    reject: { bg: "#B33A2E", color: "#fff", Icon: XCircle },
    reopen: { bg: "#fff", color: "#1F1F1F", border: "#E5E0DA", Icon: RotateCcw },
};

// variant: "buttons" (detail page) | "menu" (list row)
export function InstanceStatusActions({ instance, role, variant = "buttons", onChanged, beforeSubmit, size = "md" }) {
    const [busy, setBusy] = useState(false);
    const [rejectFor, setRejectFor] = useState(null);   // transition pending a reason
    const [reason, setReason] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [menuOpen]);

    const trans = transitionsFor(instance.status, role, instance);
    if (!trans.length) return null;

    const apply = async (to, note) => {
        setBusy(true);
        try {
            if (to === "Submitted") {
                if (beforeSubmit) { const ok = await beforeSubmit(); if (ok === false) { setBusy(false); return; } }
                await api.post(`/forms/instances/${instance.id}/submit`);
            } else {
                await api.post(`/forms/instances/${instance.id}/status`, { status: to, note: note || null });
            }
            toast.success(to === "Draft" ? "Reopened for editing" : `Marked ${to}`);
            await onChanged?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Status change failed");
        }
        setBusy(false);
        setMenuOpen(false);
    };

    const start = (t) => {
        if (t.kind === "reject") { setReason(""); setRejectFor(t); setMenuOpen(false); }
        else apply(t.to);
    };

    const pad = size === "sm" ? "px-2 py-1 text-[11.5px]" : "px-3 py-1.5 text-[13px]";

    const rejectDialog = rejectFor && (
        <div data-testid="instance-reject-dialog" className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(10,10,10,0.5)" }} onClick={() => setRejectFor(null)}>
            <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-2xl border border-[#E5E0DA]" onClick={(e) => e.stopPropagation()}>
                <div className="text-[14px] font-bold text-[#1F1F1F] flex items-center gap-2"><XCircle size={16} style={{ color: "#B33A2E" }} /> Reject form</div>
                <p className="text-[12px] text-[#6F6A64] mt-1">Add a reason so the author knows what to fix (optional).</p>
                <textarea data-testid="instance-reject-reason" autoFocus rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Section 3 photos missing; re-check fire door gaps."
                    className="w-full mt-2 text-[13px] rounded-lg border border-[#E5E0DA] px-2.5 py-2" />
                <div className="flex justify-end gap-2 mt-3">
                    <button onClick={() => setRejectFor(null)} className="text-[12px] px-3 py-1.5 rounded-lg border border-[#E5E0DA] text-[#6F6A64]">Cancel</button>
                    <button data-testid="instance-reject-confirm" disabled={busy} onClick={() => { const r = rejectFor; setRejectFor(null); apply(r.to, reason); }}
                        className="text-[12px] px-3 py-1.5 rounded-lg text-white disabled:opacity-60" style={{ background: "#B33A2E" }}>Reject</button>
                </div>
            </div>
        </div>
    );

    if (variant === "menu") {
        return (
            <div className="relative inline-block" ref={menuRef}>
                <button data-testid={`instance-status-menu-${instance.id}`} disabled={busy} onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-[#E5E0DA] text-[11.5px] font-semibold text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-50">
                    {busy ? <Loader2 className="animate-spin" size={12} /> : <ChevronDown size={12} />} Change
                </button>
                {menuOpen && (
                    <div className="absolute right-0 mt-1 z-50 w-40 bg-white border border-[#E5E0DA] rounded-lg shadow-lg py-1" onClick={(e) => e.stopPropagation()}>
                        {trans.map((t) => {
                            const cfg = KIND_BTN[t.kind];
                            const Icon = cfg.Icon;
                            return (
                                <button key={t.to} data-testid={`instance-status-${t.kind}-${instance.id}`} onClick={() => start(t)}
                                    className="w-full text-left px-3 py-1.5 text-[12.5px] flex items-center gap-2 hover:bg-[#F6F4F0]" style={{ color: t.kind === "reject" ? "#B33A2E" : "#1F1F1F" }}>
                                    <Icon size={13} /> {t.label}
                                </button>
                            );
                        })}
                    </div>
                )}
                {rejectDialog}
            </div>
        );
    }

    return (
        <>
            {trans.map((t) => {
                const cfg = KIND_BTN[t.kind];
                const Icon = cfg.Icon;
                return (
                    <button key={t.to} data-testid={`instance-status-${t.kind}`} disabled={busy} onClick={() => start(t)}
                        className={`inline-flex items-center gap-1.5 ${pad} font-semibold rounded-lg disabled:opacity-60`}
                        style={cfg.border ? { background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` } : { background: cfg.bg, color: cfg.color }}>
                        {busy ? <Loader2 className="animate-spin" size={13} /> : <Icon size={13} />} {t.label}
                    </button>
                );
            })}
            {rejectDialog}
        </>
    );
}
